import { createContext, useCallback, useContext, useMemo, useState, useEffect } from "react";
import {
  COOKIE_CONSENT_STORAGE_KEY,
  defaultConsentRecord,
  parseConsentRecord,
} from "../constants/cookieConsent";
import { loadPosthog, disablePosthog } from "../utils/posthogLoader";

const CookieConsentContext = createContext(null);

function readStored() {
  if (typeof window === "undefined") return null;
  try {
    return parseConsentRecord(localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

function persist(record) {
  const next = { ...record, ts: new Date().toISOString() };
  try {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

export function CookieConsentProvider({ children }) {
  const [stored, setStored] = useState(() => readStored());
  const [prefsOpen, setPrefsOpen] = useState(false);

  const hasDecision = !!stored?.ts;
  const analytics = !!stored?.analytics;
  const marketing = !!stored?.marketing;

  useEffect(() => {
    if (!hasDecision || !analytics) {
      disablePosthog();
      return;
    }
    loadPosthog().catch(() => {});
  }, [hasDecision, analytics]);

  const acceptAll = useCallback(() => {
    const r = persist({ ...defaultConsentRecord(), analytics: true, marketing: true });
    setStored(r);
    setPrefsOpen(false);
  }, []);

  const refuseAll = useCallback(() => {
    const r = persist({ ...defaultConsentRecord(), analytics: false, marketing: false });
    setStored(r);
    disablePosthog();
    setPrefsOpen(false);
  }, []);

  const saveCustom = useCallback((nextAnalytics, nextMarketing) => {
    const r = persist({
      ...defaultConsentRecord(),
      analytics: !!nextAnalytics,
      marketing: !!nextMarketing,
    });
    setStored(r);
    if (!r.analytics) disablePosthog();
    setPrefsOpen(false);
  }, []);

  const openPreferences = useCallback(() => setPrefsOpen(true), []);
  const closePreferences = useCallback(() => setPrefsOpen(false), []);

  const value = useMemo(
    () => ({
      hasDecision,
      analytics,
      marketing,
      prefsOpen,
      openPreferences,
      closePreferences,
      acceptAll,
      refuseAll,
      saveCustom,
    }),
    [
      hasDecision,
      analytics,
      marketing,
      prefsOpen,
      openPreferences,
      closePreferences,
      acceptAll,
      refuseAll,
      saveCustom,
    ],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsent doit être utilisé sous CookieConsentProvider");
  }
  return ctx;
}
